<template>
	<div v-if="userProfile.isBundleEmail" class="app-bundle">
		<div class="button" v-if="assets.length !== 0" @click="setShowDialog()" :style="[{'background-color': headerColor}]">
			<span class="icon fastic-bundle" :style="[{'color': menuColor}]"></span>
			<div class="count" :style="[{'border-color': headerColor}]">{{ assets.length }}</div>
		</div>
		<div v-if="isShowDialog" class="overlay">
			<div class="layer" data-size="small">
				<div class="top" :style="[{'background-color': headerColor}]">
					<div @click="setShowDialog()" class="close icon fastic-close" :style="[{'color': menuColor}]"></div>
					<div class="title" :style="[{'color': menuColor}]">{{ localizer.get("PrivateArea.Common.Bundle_Header_Title") }}</div>
				</div>
				<div class="content">
					<div class="add form">
						<div class="assets">
							<div class="label">{{ assets.length }} {{ localizer.get("PrivateArea.Common.RendersSelected") }}:</div>
							<div class="list-item">
								<div v-for="(asset, index) in assets" :key="index" class="item">
									{{ asset.title }}
									<div @click="removeAsset(asset.idMaster)" class="remove">{{ localizer.get("PrivateArea.Common.Remove") }}</div>
								</div>
							</div>
						</div>
						<div class="label" for="Email">{{ localizer.get("PrivateArea.Common.Bundle_RendersList_Title") }}</div>
						<div class="item">
							<div class="input">
								<input type="text" id="Email" v-model.lazy="formData.email" :placeholder="localizer.get('PrivateArea.Common.Asset_ProfileRelationList_EmailPlaceholder')" :class="[{'input-validation-error': isValidationError('Email')}, 'textbox']" />
							</div>
						</div>
						<div class="item">
							<div class="input">
								<label for="ExpirationDays">{{ localizer.get("PrivateArea.Common.Expiration") }}</label>
								<select id="ExpirationDays" v-model="formData.expirationDays" :class="[{'input-validation-error': isValidationError('ExpirationDays')}, 'dropdown']">
									<option v-for="(expirationDay, index) in userProfile.expirationDays" :key="index" :value="expirationDay.value">
										{{ expirationDay.title }}
									</option>
								</select>
							</div>
						</div>
						<div v-if="!isPrivateDisabled && formData.isDirectLink !== true" class="item">
							<div class="checkbox" data-info>
								<div class="label">
									<input type="checkbox" id="IsPrivate" :disabled="isPrivateDisabled" v-model.lazy="formData.isPrivate" :placeholder="localizer.get('PrivateArea.Common.Asset_ProfileRelationList_EmailPlaceholder')" :class="[{'input-validation-error': isValidationError('IsPrivate')}, 'checkbox']" />
									<label for="IsPrivate">{{ localizer.get("PrivateArea.Common.RequestLogin") }}</label>
								</div>
								<div class="info">
									<span class="icon fastic-info"></span>
									<div class="tooltip">{{ localizer.get("PrivateArea.Common.PrivateDescriptionTooltip") }}</div>
								</div>
							</div>
						</div>
						<div class="item">
							<div class="input">
								<textarea id="MessageNotes" v-model.lazy="formData.messageNotes" :placeholder="localizer.get('PrivateArea.Common.ProfileRelationList_Notes_Placeholder')" :class="[{'input-validation-error': isValidationError('MessageNotes')}, 'textarea']"></textarea>
							</div>
						</div>
						<div v-if="formData.isPrivate" class="item">
							<div class="message">
								<span class="icon fastic-info"></span>
								<span class="text">{{ localizer.get("PrivateArea.Common.PrivateDefault") }}</span>
							</div>
						</div>
						<div class="item">
							<div v-if="!isLoading" @click="setBundleEmail()" class="button" :style="[{'background-color': accentColor}]">{{ localizer.get("PrivateArea.Common.Asset_ProfileRelationList_SetProfileRelation_Button") }}</div>
							<div v-else class="button load" :style="[{'background-color': accentColor}]"><div class="loader"></div></div>
						</div>
						<div v-if="validationErrors.length !== 0" class="validation-summary-errors">
							<ul>
								<li v-for="(validationError) in validationErrors" :key="validationError.name">{{ validationError.message }}</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import Axios from "axios";
import FormHelper from "@Scripts/Common/FormHelper";
import Localizer from "@Scripts/Common/Localizer";
import GTagHelper from "@Scripts/Infrastructure/GTagHelper";

const props = defineProps<{
	accentColor: string;
	headerColor: string;
	menuColor: string;
}>();

const store = useStore();
const localizer = Localizer;
const isLoading = ref(false);
const isSuccess = ref(false);
const formData = ref<any>({});
const validationErrors = ref<any[]>([]);
const userProfile = ref<any>({ expirationDay: [], shareModes: [], isBundleEmail: false });
const assets = ref<any[]>([]);
const isPrivateDisabled = ref(false);
const isShowDialog = ref(false);
const routes = (window as any).App.routes;

const emailBundleIds = computed(() => store.getters["privateAreaBundleEmailStore/emailBundleIds"]);

watch(emailBundleIds, (newValue) => {
	getBundleEmailData();
});

onMounted(() => {
	store.dispatch("privateAreaBundleEmailStore/getEmailBundleIds");

	getBundleEmailData();
});

async function getBundleEmailData() {
	try {
		const response = (await Axios.post(routes.PrivateArea_Asset_GetBundleEmailData, { assetIdMasters: emailBundleIds.value })).data;
		userProfile.value = response.userProfile;
		assets.value = response.assets;
		formData.value.expirationDays = userProfile.value.expirationDays[0].value;
	} catch (error) { }
}

async function setBundleEmail() {
	FormHelper.removeFocus();
	validationErrors.value = [];

	isLoading.value = true;

	formData.value.assetIdMasters = emailBundleIds.value;

	try {
		await Axios.post(routes.PrivateArea_Asset_SetBundleEmail, formData.value, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken() } });

		let requestLogin = formData.value.isPrivate ? "login" : "public";

		GTagHelper.sendEvent("form_submit", "render_share", formData.value.expirationDays + "_" + requestLogin, assets.value.length);

		formData.value = {};

		isSuccess.value = true;

		store.dispatch("accountAreaToastStore/onAddToast", { message: localizer.get("PrivateArea.Common.Asset_ProfileRelationList_SetProfileRelation_Success"), type: "Success" });

		store.commit("privateAreaBundleEmailStore/setEmailBundleIds", []);
		store.dispatch("privateAreaBundleEmailStore/saveEmailBundleIds");

		setShowDialog();
	} catch (error) {
		if (error.response && error.response.status === 400) {
			validationErrors.value = FormHelper.getValidationErrors(error.response.data);
		}
	}

	isLoading.value = false;
}

function removeAsset(assetIdMaster: string) {
	store.commit("privateAreaBundleEmailStore/setEmailBundleIds", emailBundleIds.value.filter(x => x !== assetIdMaster));

	store.dispatch("privateAreaBundleEmailStore/saveEmailBundleIds");
}

function isValidationError(controlName: string): boolean {
	return FormHelper.isControlValidationError(controlName, validationErrors.value);
}

function setShowDialog() {
	isShowDialog.value = !isShowDialog.value;
}
</script>
